import React, { createRef, FC, RefObject, useCallback, useEffect, useState } from 'react';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import classNames from 'classnames';

import useScreenRecognition from 'hooks/useScreenRecognition';
import DangerouslySetInnerHtml from 'common/DangerouslySetInnerHtml';
import Title from 'common/Title';

import Helpers from './helpers';
import { IPropsCustomTabs, ICustomTabsItem } from './model';
import './CustomTabs.scss';

const CustomTabs: FC<IPropsCustomTabs> = ({
  items,
  selectedIndex,
  onSelectCallback,
  tabListWrapperClassName,
  showResultsScrollTargetId,
  tabListId,
  uniqueAttr,
  isCustomTabsTableMode,
  customTabsDescription,
  title,
}) => {
  const [tabsHeaderRef, setTabsHeaderRef] = useState<RefObject<HTMLButtonElement>[]>([]);
  const { isMobile } = useScreenRecognition();

  useEffect(() => {
    if (!items.length || !isCustomTabsTableMode) {
      return;
    }
    const headersRefs: RefObject<HTMLButtonElement>[] = items.map(() =>
      createRef<HTMLButtonElement>()
    );
    setTabsHeaderRef(headersRefs);
  }, []);

  const handleScrollOnSelect = useCallback((index: number, event: Event) => {
    const target = event.target as HTMLElement;
    const listElement = target?.parentElement?.parentElement;

    if (!listElement || !listElement.classList.contains('react-tabs__tab-list')) {
      return;
    }

    listElement.scrollTo({
      left: Helpers.calculateLeftScroll(listElement, target, index),
      behavior: 'smooth',
    });
  }, []);

  const handleSelectCallback = useCallback((index: number, _: number, event: Event) => {
    onSelectCallback(index);
    handleScrollOnSelect(index, event);
  }, []);

  return (
    <>
      {title ? <Title dataTestAttr="customTabsTitle">{title}</Title> : null}
      <DangerouslySetInnerHtml html={customTabsDescription} className="tab-list-text" />
      <Tabs selectedIndex={selectedIndex} onSelect={handleSelectCallback}>
        <div className={classNames('tab-list-wrapper', tabListWrapperClassName)}>
          <TabList {...(tabListId && { id: tabListId })}>
            {items.map((item: ICustomTabsItem, index: number) => (
              <Tab
                key={item.title}
                tabIndex="0"
                {...(item.id && uniqueAttr && { [uniqueAttr]: item.id })}
                className="react-tabs__tab"
              >
                <button
                  className="tab-opener"
                  type="button"
                  aria-label={item.ariaLabel}
                  tabIndex={-1}
                  ref={tabsHeaderRef?.[index] || null}
                >
                  {item.title}
                </button>
              </Tab>
            ))}
          </TabList>
        </div>

        {showResultsScrollTargetId ? <div id={showResultsScrollTargetId} /> : null}

        {items.map((item: ICustomTabsItem, index: number) => (
          <TabPanel
            key={item.title}
            {...(!isMobile &&
              isCustomTabsTableMode && {
                style: { width: `${tabsHeaderRef[index]?.current?.offsetWidth}px` },
              })}
            className={
              isCustomTabsTableMode
                ? classNames('react-tabs__tab-panel', {
                    'react-tabs__tab-panel--first': index === 0,
                    'react-tabs__tab-panel--second': index === 1,
                  })
                : 'react-tabs__tab-panel'
            }
          >
            {item.content}
          </TabPanel>
        ))}
      </Tabs>
    </>
  );
};

export default CustomTabs;
